/**
 * Player posters on the Final Results. The results screen only lists the players' names; tapping a name opens that
 * player's poster full screen, with the game centre's logo and name at the top left. Shared by the host's results
 * screen and the guests' (both build the posters with `buildPlayerPosters` from the same round history), so the two
 * can never show different things. On-screen only: nothing is sent anywhere.
 */

import { formatDuration } from '../../game/summary';
import type { PlayerPoster } from '../../game/posters';
import type { Branding } from '../branding';
import { deltaSpan, esc } from '../helpers';
import { clearPosterOfferSeen, getDefaultWinner, getMyPlayers, markPosterOfferSeen, posterOfferSeen } from '../viewerIdentity';

const round = (n: number) => `round #${n}`;

function line(label: string, body: string): string {
  return `<div class="poster-line"><span class="poster-label">${esc(label)}</span><span class="poster-body">${body}</span></div>`;
}

/** What the poster says about the hands this player won — or a friendly line when there is nothing to show. */
function handsHtml(p: PlayerPoster): string {
  if (p.roundsWon === 0) return '<p class="poster-empty">No winning hand this game — next time!</p>';
  const lines: string[] = [];
  if (p.star) {
    const fan = `${p.star.fan} fan${p.star.limit ? ', limit' : ''}`;
    lines.push(line('Star pattern', `${esc(p.star.label)} <span class="muted">(${fan})</span><br />${round(p.star.round)} · ${deltaSpan(p.star.points)}`));
  }
  if (p.stack) {
    lines.push(
      line(
        'Most combo win',
        `${p.stack.count} combo${p.stack.count === 1 ? '' : 's'} <span class="muted">(${esc(p.stack.labels.join(', '))})</span><br />${round(p.stack.round)} · ${deltaSpan(p.stack.points)}`,
      ),
    );
  }
  if (lines.length === 0) return '<p class="poster-empty">No scoring pattern on your winning hands this game.</p>';
  return lines.join('');
}

/** The records this player holds — only ever the ones they actually hold. */
function recordsHtml(p: PlayerPoster): string {
  const lines: string[] = [];
  if (p.biggestWin) {
    lines.push(line('Biggest win', `${p.biggestWin.fan} fan${p.biggestWin.limit ? ' (limit)' : ''}<br />${round(p.biggestWin.round)} · ${deltaSpan(p.biggestWin.points)}`));
  }
  if (p.longestRound) {
    lines.push(line('Longest round', `${formatDuration(p.longestRound.durationMs)}<br />${round(p.longestRound.round)} · ${deltaSpan(p.longestRound.points)}`));
  }
  if (p.fastestRound) {
    lines.push(line('Fastest round', `${formatDuration(p.fastestRound.durationMs)}<br />${round(p.fastestRound.round)} · ${deltaSpan(p.fastestRound.points)}`));
  }
  return lines.length > 0 ? `<div class="poster-records">${lines.join('')}</div>` : '';
}

/** The game centre's logo and name, top left — the same logo the rest of the app shows (or its placeholder). */
function brandHtml(brand: Branding): string {
  const logo = brand.logoDataUrl
    ? `<img src="${brand.logoDataUrl}" alt="" class="poster-logo" />`
    : '<span class="poster-logo poster-logo-placeholder" aria-hidden="true">🀄</span>';
  return `<div class="poster-brand">${logo}<span class="poster-brand-name">${esc(brand.name)}</span></div>`;
}

/** One poster's HTML (exported for tests). */
export function posterHtml(p: PlayerPoster, brand: Branding, footer = ''): string {
  return `
    <article class="poster${p.isWinner ? ' poster-winner' : ''}" aria-label="${esc(p.name)}'s game">
      <header class="poster-top">
        ${brandHtml(brand)}
        <span class="poster-rank">#${p.rank}</span>
      </header>
      <h2 class="poster-name">${esc(p.name)}</h2>
      <div class="poster-remark poster-remark-${p.remark.kind}">
        <span class="poster-icon" aria-hidden="true">${p.remark.icon}</span>
        <span>${esc(p.remark.text)}</span>
      </div>
      <div class="poster-stats">
        <div><strong>${p.roundsWon}</strong><span class="poster-caption">of ${p.roundsPlayed} round${p.roundsPlayed === 1 ? '' : 's'} won</span></div>
        <div><strong>${deltaSpan(p.points)}</strong><span class="poster-caption">points</span></div>
      </div>
      ${handsHtml(p)}
      ${recordsHtml(p)}
      ${footer ? `<footer class="poster-footer">${esc(footer)}</footer>` : ''}
    </article>`;
}

/**
 * The card on the results screen: just the players' names. Empty for a game with no rounds (no posters). The winner's
 * name carries the trophy.
 */
export function postersCardHtml(posters: PlayerPoster[]): string {
  if (posters.length === 0) return '';
  return `
    <div class="card" id="posters">
      <h2>Player posters</h2>
      <p class="muted">Tap a name to see that player's poster.</p>
      <div class="poster-tabs">
        ${posters
          .map(
            (p, i) =>
              `<button type="button" class="poster-tab" data-poster-tab="${i}" aria-haspopup="dialog">${p.isWinner ? '🏆 ' : ''}${esc(p.name)}</button>`,
          )
          .join('')}
      </div>
    </div>`;
}

let overlay: HTMLDivElement | undefined;
let onKey: ((e: KeyboardEvent) => void) | undefined;
let bodyOverflow = '';

function closePoster(): void {
  overlay?.remove();
  overlay = undefined;
  if (onKey) document.removeEventListener('keydown', onKey);
  onKey = undefined;
  document.body.style.overflow = bodyOverflow;
}

/** Opens one player's poster full screen. Previous/next move between players; Escape or the close button leaves. */
export function openPoster(posters: PlayerPoster[], start: number, brand: Branding, footer: string): void {
  closePoster();
  let index = Math.max(0, Math.min(posters.length - 1, start));

  const el = document.createElement('div');
  el.className = 'poster-overlay';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', 'Player poster');

  const draw = () => {
    const p = posters[index]!;
    el.innerHTML = `
      <div class="poster-frame">
        <button type="button" class="poster-close" aria-label="Close poster">✕</button>
        ${posterHtml(p, brand, footer)}
        <nav class="poster-nav" aria-label="Other players">
          <button type="button" class="poster-prev secondary"${index === 0 ? ' disabled' : ''}>‹ ${index > 0 ? esc(posters[index - 1]!.name) : 'Previous'}</button>
          <button type="button" class="poster-next secondary"${index === posters.length - 1 ? ' disabled' : ''}>${index < posters.length - 1 ? esc(posters[index + 1]!.name) : 'Next'} ›</button>
        </nav>
      </div>`;
    el.querySelector<HTMLButtonElement>('.poster-close')!.addEventListener('click', closePoster);
    el.querySelector<HTMLButtonElement>('.poster-prev')!.addEventListener('click', () => go(-1));
    el.querySelector<HTMLButtonElement>('.poster-next')!.addEventListener('click', () => go(1));
    el.scrollTop = 0;
  };
  const go = (step: number) => {
    const next = index + step;
    if (next < 0 || next >= posters.length) return;
    index = next;
    draw();
  };

  onKey = (e) => {
    if (e.key === 'Escape') closePoster();
    else if (e.key === 'ArrowLeft') go(-1);
    else if (e.key === 'ArrowRight') go(1);
  };
  document.addEventListener('keydown', onKey);

  bodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  draw();
  document.body.appendChild(el);
  overlay = el;
  el.querySelector<HTMLButtonElement>('.poster-close')!.focus();
}

/** Makes each name open its poster. A no-op without the card. */
export function wirePosters(root: HTMLElement, posters: PlayerPoster[], brand: Branding, footer: string): void {
  root.querySelectorAll<HTMLButtonElement>('#posters .poster-tab').forEach((tab) => {
    tab.addEventListener('click', () => openPoster(posters, Number(tab.dataset.posterTab), brand, footer));
  });
}

/** The poster of the player this device belongs to: matched by name, ignoring case and stray spaces. */
export function findOwnPoster(posters: PlayerPoster[], viewer: string | undefined): number {
  const who = (viewer ?? '').trim().toLowerCase();
  if (!who) return -1;
  return posters.findIndex((p) => p.name.trim().toLowerCase() === who);
}

/** What this device knows about who it plays for (see `viewerIdentity.ts`). */
export interface WhoAmI {
  /** `game:<id>` on the host, `share:<token>` on a guest — also what the "shown once" flag is kept under. */
  key: string;
  /** The nickname the phone signed in with at a leaderboard table, if any. */
  nickname?: string;
}

/**
 * The posters this device may show, in poster order: the players whose rounds it submitted, the one it was told is "me", and the
 * player it signed in as. One entry = this phone is one player; several = it helped record rounds for others.
 */
export function ownPosterIndices(posters: PlayerPoster[], who: WhoAmI): number[] {
  const ids = new Set(getMyPlayers(who.key));
  const def = getDefaultWinner(who.key);
  if (def) ids.add(def);
  const byName = findOwnPoster(posters, who.nickname);
  const out: number[] = [];
  posters.forEach((p, i) => {
    if (ids.has(p.player) || i === byName) out.push(i);
  });
  return out;
}

/** The pop-up's words: encouraging for one player, "All" when this phone helped several. */
export function offerCopy(posters: PlayerPoster[], indices: number[]): { title: string; text: string; button: string; emoji: string } {
  if (indices.length === 1) {
    const mine = posters[indices[0]!]!;
    return {
      emoji: mine.isWinner ? '🏆' : '🎉',
      title: mine.isWinner ? `You won, ${mine.name}!` : `Nice game, ${mine.name}!`,
      text: 'Your game poster is ready — your score, your best hand and your records.',
      button: 'View my poster',
    };
  }
  return {
    emoji: '🎉',
    title: 'Nice game, All!',
    text: 'You recorded rounds for more than one player on this phone. Choose whose poster to see.',
    button: 'View poster',
  };
}

/** How long after the results appear before the poster pop-up does (Indra, 2026-09-27): long enough to see the results first. */
export const OFFER_DELAY_MS = 2000;
/**
 * The pop-up covers the whole screen from the moment it starts to fade in, so nothing behind it can be tapped; its own buttons ignore
 * taps for this long, so a tap that was already on its way to the screen behind cannot land on "Not now" / "View my poster".
 */
export const OFFER_TAP_GUARD_MS = 400;

/** A small modal card with the same look for both pop-ups; returns its box and a `close`. `guard` = its buttons wait a moment before they react. */
function popup(html: string, guard = false): { box: HTMLDivElement; close: () => void } {
  const box = document.createElement('div');
  box.className = 'poster-offer';
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-modal', 'true');
  box.setAttribute('aria-label', 'Your poster');
  box.innerHTML = `<div class="poster-offer-card${guard ? ' poster-offer-guarded' : ''}">${html}</div>`;
  if (guard) setTimeout(() => box.querySelector('.poster-offer-guarded')?.classList.remove('poster-offer-guarded'), OFFER_TAP_GUARD_MS);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  const close = () => {
    box.remove();
    document.removeEventListener('keydown', onKey);
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(box);
  return { box, close };
}

/**
 * When the game has ended, offers THIS device its own player's poster in a small pop-up: "View my poster" opens it full screen,
 * "Not now" closes the pop-up (the names in the posters card stay as they are). If the phone knows several players (it helped
 * record rounds for others) the pop-up says "Nice game, All!" and "View poster" opens a second pop-up to choose whose. Made once per
 * game per device, and only when the phone knows at least one player that has a poster.
 */
export function offerOwnPoster(posters: PlayerPoster[], who: WhoAmI, brand: Branding, footer: string): void {
  const indices = ownPosterIndices(posters, who);
  if (indices.length === 0 || posterOfferSeen(who.key) || scheduledOffers.has(who.key)) return;
  // A short pause first, so the results are seen before anything covers them. One timer per game however often the screen redraws.
  scheduledOffers.set(
    who.key,
    setTimeout(() => {
      scheduledOffers.delete(who.key);
      // The player left the results in the meantime (New Game, Past Games, back to the game): nothing to show now — and the offer has not
      // been used up, so the next time these results are shown it is made.
      if (!document.getElementById('posters') || posterOfferSeen(who.key)) return;
      markPosterOfferSeen(who.key);
      showOffer(posters, indices, brand, footer);
    }, OFFER_DELAY_MS),
  );
}

const scheduledOffers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * The game is being played again: forget that its offer was made (and cancel one that was about to be), so that ending the game again
 * offers the poster again — the same way the confetti comes back each time.
 */
export function resetPosterOffer(key: string | undefined): void {
  if (!key) return;
  const timer = scheduledOffers.get(key);
  if (timer !== undefined) clearTimeout(timer);
  scheduledOffers.delete(key);
  clearPosterOfferSeen(key);
}

function showOffer(posters: PlayerPoster[], indices: number[], brand: Branding, footer: string): void {
  const copy = offerCopy(posters, indices);
  const { box, close } = popup(
    `
      <div class="poster-offer-emoji" aria-hidden="true">${copy.emoji}</div>
      <h2>${esc(copy.title)}</h2>
      <p class="muted">${esc(copy.text)}</p>
      <button type="button" class="poster-offer-view" style="width:100%">${esc(copy.button)}</button>
      <button type="button" class="poster-offer-close ghost" style="width:100%;margin-top:0.5rem">Not now</button>`,
    true,
  );
  box.querySelector<HTMLButtonElement>('.poster-offer-view')!.addEventListener('click', () => {
    close();
    if (indices.length === 1) return openPoster(posters, indices[0]!, brand, footer);
    chooseOwnPoster(posters, indices, brand, footer);
  });
  box.querySelector<HTMLButtonElement>('.poster-offer-close')!.addEventListener('click', close);
  box.querySelector<HTMLButtonElement>('.poster-offer-view')!.focus();
}

/** The second pop-up: whose poster, among the players this phone recorded rounds for. */
function chooseOwnPoster(posters: PlayerPoster[], indices: number[], brand: Branding, footer: string): void {
  const { box, close } = popup(`
      <h2>Whose poster?</h2>
      <p class="muted">These are the players you recorded rounds for on this phone.</p>
      <div class="stack">
        ${indices
          .map((i) => `<button type="button" class="poster-offer-pick secondary" data-poster-index="${i}" style="width:100%">${posters[i]!.isWinner ? '🏆 ' : ''}${esc(posters[i]!.name)}</button>`)
          .join('')}
      </div>
      <button type="button" class="poster-offer-close ghost" style="width:100%;margin-top:0.5rem">Cancel</button>`);
  box.querySelectorAll<HTMLButtonElement>('.poster-offer-pick').forEach((btn) =>
    btn.addEventListener('click', () => {
      close();
      openPoster(posters, Number(btn.dataset.posterIndex), brand, footer);
    }),
  );
  box.querySelector<HTMLButtonElement>('.poster-offer-close')!.addEventListener('click', close);
  box.querySelector<HTMLButtonElement>('.poster-offer-pick')?.focus();
}

/** "Table 3 · 24 Sep 2026" — the line at the foot of every poster. */
export function posterFooter(tableId: string, endedAt: number | undefined): string {
  const date = endedAt ? new Date(endedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  return date ? `Table ${tableId} · ${date}` : `Table ${tableId}`;
}
